import { endOfDay, endOfWeek, startOfDay, startOfWeek } from 'date-fns';
import { get, getOr } from 'lodash/fp';

import ApprovalCirclePill from '../../assets/icons/ApprovedCirclePill.svg';
import ConflictCirclePill from '../../assets/icons/ConflictCirclePill.svg';
import PendingCirclePill from '../../assets/icons/PendingCirclePill.svg';
import theme from '../../theme/theme';

import { BULK_ACTION_TYPES, TIMEKEEPING_STATUSES, TIMEKEEPING_TAGS } from './constants';

export const SHORT_MESSAGES = {
  APPROVED: 'timekeeping.status.short.approved',
  CONFLICT: 'timekeeping.status.short.conflict',
  PENDING: 'timekeeping.status.short.pending',
  DISPUTED: 'timekeeping.status.short.disputed',
  RESOLVED: 'timekeeping.status.short.resolved',
  FINALIZED: 'timekeeping.status.short.finalized',
  OVERTIME: 'timekeeping.status.short.overtime',
};

const MESSAGES = {
  APPROVED: 'timekeeping.status.approved',
  CONFLICT: 'timekeeping.status.conflictFlag',
  PENDING: 'timekeeping.status.pending',
  DISPUTED: 'timekeeping.status.disputed',
  RESOLVED: 'timekeeping.status.resolved',
  FINALIZED: 'timekeeping.status.finalized',
  OVERTIME: 'timekeeping.status.overtime',
};

export const handleLabelProps = (status, nameIsShortened) => {
  let backgroundColor;
  let color;
  let text;
  let Icon;

  switch (status) {
    case TIMEKEEPING_STATUSES.APPROVED:
      backgroundColor = get(['label', 'timekeeping', 'bgColor', 'approved'], theme);
      color = get(['label', 'timekeeping', 'color', 'approved'], theme);
      text = !nameIsShortened ? MESSAGES.APPROVED : SHORT_MESSAGES.APPROVED;
      Icon = ApprovalCirclePill;
      break;
    case TIMEKEEPING_STATUSES.CONFLICT:
      backgroundColor = get(['label', 'timekeeping', 'bgColor', 'conflict'], theme);
      color = get(['label', 'timekeeping', 'color', 'conflict'], theme);
      text = !nameIsShortened ? MESSAGES.CONFLICT : SHORT_MESSAGES.CONFLICT;
      Icon = ConflictCirclePill;
      break;
    case TIMEKEEPING_STATUSES.PENDING:
      backgroundColor = get(['label', 'timekeeping', 'bgColor', 'pending'], theme);
      color = get(['label', 'timekeeping', 'color', 'pending'], theme);
      text = !nameIsShortened ? MESSAGES.PENDING : SHORT_MESSAGES.PENDING;
      Icon = PendingCirclePill;
      break;
    case TIMEKEEPING_STATUSES.DISPUTED:
      backgroundColor = get(['label', 'timekeeping', 'bgColor', 'disputed'], theme);
      color = get(['label', 'timekeeping', 'color', 'disputed'], theme);
      text = !nameIsShortened ? MESSAGES.DISPUTED : SHORT_MESSAGES.DISPUTED;
      Icon = ConflictCirclePill;
      break;
    case TIMEKEEPING_STATUSES.FINALIZED:
      backgroundColor = get(['label', 'timekeeping', 'bgColor', 'finalized'], theme);
      color = get(['label', 'timekeeping', 'color', 'finalized'], theme);
      text = !nameIsShortened ? MESSAGES.FINALIZED : SHORT_MESSAGES.FINALIZED;
      Icon = ApprovalCirclePill;
      break;
    case TIMEKEEPING_STATUSES.RESOLVED:
      backgroundColor = get(['label', 'timekeeping', 'bgColor', 'resolved'], theme);
      color = get(['label', 'timekeeping', 'color', 'resolved'], theme);
      text = !nameIsShortened ? MESSAGES.RESOLVED : SHORT_MESSAGES.RESOLVED;
      Icon = ApprovalCirclePill;
      break;
    case TIMEKEEPING_STATUSES.OVERTIME:
      backgroundColor = get(['label', 'timekeeping', 'bgColor', 'overtime'], theme);
      color = get(['label', 'timekeeping', 'color', 'overtime'], theme);
      text = !nameIsShortened ? MESSAGES.OVERTIME : SHORT_MESSAGES.OVERTIME;
      Icon = ConflictCirclePill;
      break;

    default:
      throw new Error('Valid status option required');
  }

  return {
    sx: {
      backgroundColor,
      color,
    },
    sxForText: {
      fontFamily: get(['label', 'job_orders', 'font'], theme),
    },
    Icon,
    text,
  };
};

export const sumDayTotalTime = (day) => {
  const value = day.reduce(
    (accumulator, curr) => accumulator + parseFloat(getOr(0, ['timecard', 'totalHours'], curr), 10),
    0
  );
  return value;
};

export const generateStatusData = (data, selectedTimeCard) => {
  const daysData = data?.days;

  const days = Object.keys(daysData);
  const returnObj = {};
  const approveList = [];
  days.forEach((day) => {
    returnObj[day] = sumDayTotalTime(daysData[day]);

    daysData[day].forEach((time) => {
      approveList.push(get(['timecard', 'status'], time));
    });
  });
  returnObj.status = {
    label:
      (approveList.includes(TIMEKEEPING_STATUSES.DISPUTED) && TIMEKEEPING_STATUSES.DISPUTED) ||
      (approveList.includes(TIMEKEEPING_STATUSES.PENDING) && TIMEKEEPING_STATUSES.PENDING) ||
      (approveList.includes(TIMEKEEPING_STATUSES.APPROVED) && TIMEKEEPING_STATUSES.APPROVED) ||
      (approveList.includes(TIMEKEEPING_STATUSES.RESOLVED) && TIMEKEEPING_STATUSES.RESOLVED) ||
      TIMEKEEPING_STATUSES.FINALIZED,
    detailViewStatus: !!selectedTimeCard,
  };

  return returnObj;
};

export const handleExport = ({ csv, fileName }) => {
  const blob = new Blob([csv], { type: 'text/csv;charset=utf-8;' });
  const link = document.createElement('a');
  const url = URL.createObjectURL(blob);
  link.setAttribute('href', url);
  link.setAttribute('download', fileName);
  link.style.visibility = 'hidden';
  document.body.appendChild(link);
  link.click();
  document.body.removeChild(link);
};

const LABEL_PRIORITY_ORDER = [
  TIMEKEEPING_STATUSES.DISPUTED,
  TIMEKEEPING_STATUSES.PENDING,
  TIMEKEEPING_STATUSES.APPROVED,
  TIMEKEEPING_STATUSES.RESOLVED,
  TIMEKEEPING_STATUSES.FINALIZED,
];

export const generateCandidateHoursData = (timecard, selectedTimeCard) => {
  // A hashmap, key is date as string (ex. 11/26/2023) - value is array of timecards
  const daysData = timecard?.days;

  const days = Object.keys(daysData);
  const returnObj = {};
  // For every day in the week
  days.forEach((day) => {
    // If the day has any data (timecards), get the total numver of hours for that day. If no timecards exist, display a '-'
    returnObj[day] = daysData[day].length > 0 ? sumDayTotalTime(daysData[day]) : '-';
  });

  returnObj.status = {
    label: LABEL_PRIORITY_ORDER.find((label) =>
      Object.values(daysData)
        .flat(Infinity)
        .some((timecardEntry) => timecardEntry?.timecard?.status === label)
    ),
    detailViewStatus: !!selectedTimeCard,
  };

  return returnObj;
};

/**
 * Converts a list of timecards into an array of placments and can return an array of candidates with the hours they worked
 * @param {Array} timecards
 * @param {Boolean} shouldReturnCandidateHours
 * @returns object with properties placement (array of IDs) & nameAndHours (array of objects with candidate Name & hours worked)
 */
export const generateBatchApprovalData = (timecards, shouldReturnCandidateHours) => {
  const placementNameAndTime = [];
  const pendingTimecardsWithValidPunchSets = timecards
    .map((card) => {
      const entriesForCurrentUser = Object.values(card?.days)
        .flat(Infinity)
        .filter(
          (dayTimecardEntry) =>
            dayTimecardEntry?.timecard?.status === 'pending' &&
            dayTimecardEntry?.timecard?.punches.length > 0 &&
            dayTimecardEntry?.timecard?.punches.some((p) => p?.out?.stamp)
        );

      if (shouldReturnCandidateHours && entriesForCurrentUser.length > 0) {
        placementNameAndTime.push({
          id: card?.candidate?.id,
          values: {
            name: card?.candidate?.name,
            hours: entriesForCurrentUser.reduce(
              (accumulator, item) => accumulator + item.timecard.totalHours,
              0
            ),
          },
        });
      }
      return entriesForCurrentUser;
    })
    .flat(Infinity);

  const returnObject = {
    placements: pendingTimecardsWithValidPunchSets.map((p) => p?.placement),
    nameAndHours: placementNameAndTime,
  };

  return returnObject;
};

export const getCardFilterPayload = (startDate, endDate) => ({
  step1: {
    status: TIMEKEEPING_STATUSES.PENDING,
    tags: TIMEKEEPING_TAGS.LOOKS_GOOD,
    startDate,
    endDate,
  },
  step2: {
    status: TIMEKEEPING_STATUSES.PENDING,
    tags: [TIMEKEEPING_TAGS.SUSPICIOUS, TIMEKEEPING_TAGS.MISSING_PUNCH].join(','),
    startDate,
    endDate,
  },
  step3: {
    status: TIMEKEEPING_STATUSES.DISPUTED,
    startDate,
    endDate,
  },
  step4: {
    status: [TIMEKEEPING_STATUSES.APPROVED, TIMEKEEPING_STATUSES.RESOLVED].join(','),
    startDate,
    endDate,
  },
});

export const getStartOfDayInEpoch = (date) => startOfDay(new Date(date)).getTime();
export const getEndOfDayInEpoch = (date) => endOfDay(new Date(date)).getTime();
export const getStartOfWeekInEpoch = (date) => startOfWeek(new Date(date)).getTime();
export const getEndOfWeekInEpoch = (date) => endOfWeek(new Date(date)).getTime();

const getMatchingTimecards = (currentItems, newItems) => {
  const currentPlacementIds = new Set(currentItems.map((item) => item.placementId));
  const matchingItems = newItems.filter((item) => currentPlacementIds.has(item.placementId));

  return matchingItems;
};

export const getCalculatedTimecardsOnUpdate = (currentItem, newItem) => {
  const matchingTimecards =
    currentItem?.timecards && newItem?.timecards
      ? getMatchingTimecards(currentItem.timecards, newItem.timecards)
      : [];

  const totalEarnings = matchingTimecards.reduce(
    (total, item) => total + (item?.timecard?.totalEarnings || 0),
    0
  );
  const totalHours = matchingTimecards.reduce(
    (total, item) => total + (item?.timecard?.totalHours || 0),
    0
  );
  const overtimeHours = matchingTimecards.reduce(
    (total, item) => total + (item?.timecard?.overtimeHours || 0),
    0
  );
  const regularHours = matchingTimecards.reduce(
    (total, item) => total + (item?.timecard?.regularHours || 0),
    0
  );

  return {
    ...newItem,
    timecards: matchingTimecards,
    totalEarnings,
    totalHours,
    overtimeHours,
    regularHours,
  };
};

export const getTimecardsWithProcessedUpdates = (weeklyTimecards, updatedDocs) =>
  weeklyTimecards.map((weeklyCard) => {
    const updatedDocument = updatedDocs.find(
      (doc) => doc.candidate?._id === weeklyCard.candidate?._id
    );

    const placementId = updatedDocument?._id;

    if (!placementId) return weeklyCard;

    const matchingCard = weeklyCard.timecards.find((tc) => tc.placementId === placementId);

    if (matchingCard && updatedDocument?.timecard) {
      matchingCard.timecard = updatedDocument?.timecard;
    }

    return weeklyCard;
  });

export const getTotalHours = (data) => data.reduce((total, item) => total + (item.hours || 0), 0);

export const extractPlacementIdsFromUserMap = (updatedTimecardMap) =>
  Object.keys(updatedTimecardMap)
    .map((key) => {
      // key is in the format `${candidateId}_${placementId}`
      const parts = key.split('_');
      if (parts.length === 1 || !updatedTimecardMap[key]) {
        // If there's only one part, it means it's a candidateId without a placementId
        return null;
      }
      return parts[parts.length - 1]; // Always get last part as placementId
    })
    .filter(Boolean);

export const getTotalHourCountFromSelectedTimecards = (selectedPlacements, timecards) => {
  let totalHours = 0;

  // If no placements are selected or no timecards, return 0
  if (!selectedPlacements?.length || !timecards?.length) {
    return 0;
  }

  // Convert selectedPlacements array to Set for faster lookup
  const selectedPlacementSet = new Set(selectedPlacements);

  // Iterate through each timecard entry
  timecards.forEach((timecardEntry) => {
    // Check each individual timecard in the timecards array
    timecardEntry.timecards?.forEach((timecard) => {
      // If this timecard's placementId is in our selected set, add its hours
      if (timecard.placementId && selectedPlacementSet.has(timecard.placementId)) {
        // Use totalHours from the timecard if available, otherwise sum punches
        if (timecard.timecard?.totalHours !== undefined) {
          totalHours += timecard.timecard.totalHours;
        } else if (timecard.timecard?.punches?.length) {
          // Fallback: sum up punch hours if totalHours isn't available
          const punchHours = timecard.timecard.punches.reduce(
            (sum, punch) => sum + (punch.hours || 0),
            0
          );
          totalHours += punchHours;
        }
      }
    });
  });

  // Return the total, rounded to 2 decimal places to avoid floating point precision issues
  return parseFloat(totalHours.toFixed(2));
};

export const getActionButtonDisableMessage = (timecards, acitonType) => {
  if (timecards.length === 0) {
    return 'timekeeping.buttonHelperText.noSelections';
  }

  // aprovals need to be on pending/disputed timecards
  if (
    acitonType === BULK_ACTION_TYPES.APPROVE &&
    timecards.filter(
      (x) =>
        x?.timecard?.status === TIMEKEEPING_STATUSES.PENDING ||
        x?.timecard?.status === TIMEKEEPING_STATUSES.DISPUTED
    ).length !== timecards?.length
  ) {
    return 'timekeeping.buttonHelperText.invalidApproveSelections';
  }

  // resolves need to be on pending/disputed timecards
  if (
    acitonType === BULK_ACTION_TYPES.RESOLVE &&
    timecards.filter(
      (x) =>
        x?.timecard?.status === TIMEKEEPING_STATUSES.PENDING ||
        x?.timecard?.status === TIMEKEEPING_STATUSES.DISPUTED
    ).length !== timecards?.length
  ) {
    return 'timekeeping.buttonHelperText.invalidResolveSelections';
  }

  // finalize need to be on approved/resolved timecards
  if (
    acitonType === BULK_ACTION_TYPES.FINALIZE &&
    timecards.filter(
      (x) =>
        x?.timecard?.status === TIMEKEEPING_STATUSES.APPROVED ||
        x?.timecard?.status === TIMEKEEPING_STATUSES.RESOLVED
    ).length !== timecards?.length
  ) {
    return 'timekeeping.buttonHelperText.invalidFinalizeSelections';
  }

  return null;
};
